import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { AppState, type AppStateStatus } from "react-native";

import { onResetSelectedChild } from "@/features/selectedChild/selectedChildSlice";
import { getConfig } from "@/getConfig";
import { getFeathersClient } from "@/lib/feathers/client";
import { s4pApi } from "@/query/apiSlice";
import { store } from "@/store";

import { unregisterPushDevice } from "@/lib/pushNotifications/registerPushDevice";
import { Parent } from "@spork/s4p-server-sdk";
import { isWithinBiometricLockGrace } from "./biometricLockGrace";
import {
    type BiometricDisplayInfo,
    type BiometricSymbolName,
    hasBiometricHardware as getHasBiometricHardware,
    isBiometricUnlockAvailable,
    isBiometricUnlockDisabled,
    loadBiometricDisplayInfo,
    setBiometricUnlockDisabled as persistBiometricUnlockDisabled,
    promptBiometricUnlock,
    shouldPromptBiometricUnlock,
} from "./biometricUnlock";
import { signInWithLocal } from "./devSignIn";
import { signInWithMicrosoft as microsoftSignIn } from "./microsoftSignIn";
import { setUnauthorizedHandler } from "./unauthorizedHandler";

type AuthContextValue = {
    isAuthenticated: boolean;
    isLoading: boolean;
    isSigningIn: boolean;
    isBiometricLocked: boolean;
    isBiometricUnlockAvailable: boolean;
    isBiometricUnlockDisabled: boolean;
    hasBiometricHardware: boolean;
    canUnlockWithBiometric: boolean;
    biometricLabel: string;
    biometricSymbol: BiometricSymbolName;
    biometricFallbackIcon: BiometricDisplayInfo["fallbackIcon"];
    signInWithMicrosoft: () => Promise<void>;
    signInWithLocal: (email: string, password: string) => Promise<void>;
    signOut: () => Promise<void>;
    setBiometricUnlockDisabled: (disabled: boolean) => Promise<void>;
    tryBiometricUnlock: () => Promise<boolean>;
    authContext: Parent;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [biometricAvailable, setBiometricAvailable] = useState(false);
    const [biometricDisabled, setBiometricDisabled] = useState(false);
    const [hasBiometricHardware, setHasBiometricHardware] = useState(false);
    const [canUnlockWithBiometric, setCanUnlockWithBiometric] = useState(false);
    const [biometricLabel, setBiometricLabel] = useState("Biometrics");
    const [isBiometricLocked, setIsBiometricLocked] = useState(false);
    const [biometricSymbol, setBiometricSymbol] = useState<BiometricSymbolName>(
        {
            ios: "touchid",
            android: "fingerprint",
            web: "fingerprint",
        },
    );
    const [biometricFallbackIcon, setBiometricFallbackIcon] =
        useState<BiometricDisplayInfo["fallbackIcon"]>("fingerprint");
    const [authContext, setAuthContext] = useState<Parent | null>(null);

    // True once the cold-start session restore (JWT + optional biometric gate) has finished.
    // AppState listeners ignore transitions until then to avoid racing startup auth.
    const initialRestoreDoneRef = useRef(false);
    // True while the user has unlocked biometrics for the current foreground session.
    // Cleared after the background grace period elapses; set after a successful prompt or sign-in.
    const biometricSessionUnlockedRef = useRef(false);
    // Timestamp when the app last left the foreground. Kept across inactive → background.
    const biometricLeftAtMsRef = useRef<number | null>(null);
    // True while the native biometric dialog is visible; prevents AppState re-entry during the prompt.
    const isBiometricPromptActiveRef = useRef(false);
    // Mirror of `isAuthenticated` for synchronous reads inside AppState handlers (no stale closure).
    const isAuthenticatedRef = useRef(false);
    // Mirror of `biometricAvailable` for AppState handlers without awaiting async checks.
    const biometricAvailableRef = useRef(false);
    // Mirror of `biometricDisabled` (user opted out of biometric lock) for AppState handlers.
    const biometricDisabledRef = useRef(false);

    useEffect(() => {
        isAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated]);

    useEffect(() => {
        let cancelled = false;

        loadBiometricDisplayInfo().then(({ label, symbol, fallbackIcon }) => {
            if (!cancelled) {
                setBiometricLabel(label);
                setBiometricSymbol(symbol);
                setBiometricFallbackIcon(fallbackIcon);
            }
        });

        return () => {
            cancelled = true;
        };
    }, []);

    const refreshBiometricSession = useCallback(async () => {
        const [available, disabled, canUnlock, hasHardware] = await Promise.all([
            isBiometricUnlockAvailable(),
            isBiometricUnlockDisabled(),
            shouldPromptBiometricUnlock(),
            getHasBiometricHardware(),
        ]);

        setBiometricAvailable(available);
        setBiometricDisabled(disabled);
        setCanUnlockWithBiometric(canUnlock);
        setHasBiometricHardware(hasHardware);
        biometricAvailableRef.current = available;
        biometricDisabledRef.current = disabled;
    }, []);

    const restoreAuthenticatedSession = useCallback(async () => {
        const authResponse = await getFeathersClient().reAuthenticate(true);
        setAuthContext(authResponse.authContext);
        setIsAuthenticated(true);
    }, []);

    const runBiometricGate = useCallback(async (): Promise<boolean> => {
        if (!(await shouldPromptBiometricUnlock())) {
            biometricSessionUnlockedRef.current = true;
            return true;
        }

        isBiometricPromptActiveRef.current = true;
        try {
            const unlocked = await promptBiometricUnlock();
            biometricSessionUnlockedRef.current = unlocked;
            return unlocked;
        } finally {
            isBiometricPromptActiveRef.current = false;
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        const restoreSession = async () => {
            try {
                if (!getConfig("s4pServerUrl")) {
                    return;
                }

                if (await shouldPromptBiometricUnlock()) {
                    if (!cancelled) {
                        setIsBiometricLocked(true);
                    }
                }

                if (!(await runBiometricGate())) {
                    if (!cancelled) {
                        setIsAuthenticated(false);
                        setIsBiometricLocked(false);
                    }
                    return;
                }

                await restoreAuthenticatedSession();
                if (!cancelled) {
                    setIsAuthenticated(true);
                    setIsBiometricLocked(false);
                }
            } catch {
                if (!cancelled) {
                    setIsAuthenticated(false);
                    setIsBiometricLocked(false);
                }
            } finally {
                if (!cancelled) {
                    initialRestoreDoneRef.current = true;
                    await refreshBiometricSession();
                    setIsLoading(false);
                }
            }
        };

        restoreSession();

        return () => {
            cancelled = true;
        };
    }, [
        refreshBiometricSession,
        restoreAuthenticatedSession,
        runBiometricGate,
    ]);

    useEffect(() => {
        const handleAppStateChange = async (nextState: AppStateStatus) => {
            if (
                !initialRestoreDoneRef.current ||
                isBiometricPromptActiveRef.current
            ) {
                return;
            }

            if (nextState === "inactive" || nextState === "background") {
                if (
                    isAuthenticatedRef.current &&
                    biometricAvailableRef.current &&
                    !biometricDisabledRef.current
                ) {
                    if (biometricLeftAtMsRef.current == null) {
                        biometricLeftAtMsRef.current = Date.now();
                    }
                    // Privacy overlay immediately so the app switcher does not leak content.
                    setIsBiometricLocked(true);
                }
                return;
            }

            if (nextState !== "active") {
                return;
            }

            await refreshBiometricSession();

            const leftAtMs = biometricLeftAtMsRef.current;
            biometricLeftAtMsRef.current = null;

            if (!isAuthenticatedRef.current) {
                setIsBiometricLocked(false);
                return;
            }

            if (
                !biometricAvailableRef.current ||
                biometricDisabledRef.current
            ) {
                setIsBiometricLocked(false);
                return;
            }

            const withinGrace = isWithinBiometricLockGrace(
                leftAtMs,
                Date.now(),
            );

            if (biometricSessionUnlockedRef.current && withinGrace) {
                setIsBiometricLocked(false);
                return;
            }

            biometricSessionUnlockedRef.current = false;
            setIsBiometricLocked(true);

            const unlocked = await runBiometricGate();
            if (unlocked) {
                setIsBiometricLocked(false);
            } else {
                setIsBiometricLocked(false);
                setIsAuthenticated(false);
            }

            await refreshBiometricSession();
        };

        const subscription = AppState.addEventListener(
            "change",
            handleAppStateChange,
        );
        return () => subscription.remove();
    }, [refreshBiometricSession, runBiometricGate]);

    const tryBiometricUnlock = useCallback(async () => {
        setIsBiometricLocked(true);
        const unlocked = await runBiometricGate();
        if (!unlocked) {
            setIsBiometricLocked(false);
            return false;
        }

        try {
            await restoreAuthenticatedSession();
            setIsAuthenticated(true);
            setIsBiometricLocked(false);
            await refreshBiometricSession();
            return true;
        } catch {
            setIsAuthenticated(false);
            setIsBiometricLocked(false);
            await refreshBiometricSession();
            return false;
        }
    }, [
        refreshBiometricSession,
        restoreAuthenticatedSession,
        runBiometricGate,
    ]);

    const signInWithMicrosoft = useCallback(async () => {
        setIsSigningIn(true);
        try {
            const authResponse = await microsoftSignIn();
            setAuthContext(authResponse.authContext);
            biometricSessionUnlockedRef.current = true;
            setIsAuthenticated(true);
            setIsBiometricLocked(false);
            await refreshBiometricSession();
        } finally {
            setIsSigningIn(false);
        }
    }, [refreshBiometricSession]);

    const signInWithLocalCredentials = useCallback(
        async (email: string, password: string) => {
            setIsSigningIn(true);
            try {
                const authResponse = await signInWithLocal(email, password);
                setAuthContext(authResponse.authContext);
                biometricSessionUnlockedRef.current = true;
                setIsAuthenticated(true);
                setIsBiometricLocked(false);
                await refreshBiometricSession();
            } finally {
                setIsSigningIn(false);
            }
        },
        [refreshBiometricSession],
    );

    const signOut = useCallback(async () => {
        try {
            await unregisterPushDevice();
        } catch {
            // Best-effort; continue logout even if push cleanup fails.
        }
        try {
            await getFeathersClient().logout();
        } catch {
            // Token may already be invalid (e.g. API 401); still clear local session.
        }
        store.dispatch(s4pApi.util.resetApiState());
        store.dispatch(onResetSelectedChild());
        biometricSessionUnlockedRef.current = false;
        biometricLeftAtMsRef.current = null;
        setIsAuthenticated(false);
        setIsBiometricLocked(false);
        setAuthContext(null);
        await refreshBiometricSession();
    }, [refreshBiometricSession]);

    /**
     * Sets the unauthorized handler for the Feathers client.
     * This handles is called every time Feather returns a 401 error.
     */
    useEffect(() => {
        setUnauthorizedHandler(() => {
            if (!isAuthenticatedRef.current) {
                return;
            }
            // If the user is authenticated, sign them out.
            return signOut();
        });

        return () => {
            // Clean up the unauthorized handler when the component unmounts.
            setUnauthorizedHandler(null);
        };
    }, [signOut]);

    const setBiometricUnlockDisabled = useCallback(
        async (disabled: boolean) => {
            await persistBiometricUnlockDisabled(disabled);
            if (disabled) {
                biometricSessionUnlockedRef.current = true;
                setIsBiometricLocked(false);
            }
            await refreshBiometricSession();
        },
        [refreshBiometricSession],
    );

    const value = useMemo(
        () => ({
            isAuthenticated,
            isLoading,
            isSigningIn,
            isBiometricLocked,
            isBiometricUnlockAvailable: biometricAvailable,
            isBiometricUnlockDisabled: biometricDisabled,
            hasBiometricHardware,
            canUnlockWithBiometric,
            biometricLabel,
            biometricSymbol,
            biometricFallbackIcon,
            signInWithMicrosoft,
            signInWithLocal: signInWithLocalCredentials,
            signOut,
            setBiometricUnlockDisabled,
            tryBiometricUnlock,
            authContext,
        }),
        [
            isAuthenticated,
            isLoading,
            isSigningIn,
            isBiometricLocked,
            biometricAvailable,
            biometricDisabled,
            hasBiometricHardware,
            canUnlockWithBiometric,
            biometricLabel,
            biometricSymbol,
            biometricFallbackIcon,
            signInWithMicrosoft,
            signInWithLocalCredentials,
            signOut,
            setBiometricUnlockDisabled,
            tryBiometricUnlock,
            authContext,
        ],
    );

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
}
